import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ThemeProvider } from "@/components/ThemeProvider";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { PushNotificationPrompt } from "@/components/PushNotificationPrompt";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SobreUmbanda from "./pages/SobreUmbanda";
import QuemSomos from "./pages/QuemSomos";
import TrabalhosEspirituais from "./pages/TrabalhosEspirituais";
import Normas from "./pages/Normas";
import Doacoes from "./pages/Doacoes";
import Contato from "./pages/Contato";
import Galeria from "./pages/Galeria";
import Biblioteca from "./pages/Biblioteca";
import Auth from "./pages/Auth";
import Agendamento from "./pages/Agendamento";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import Unsubscribe from "./pages/Unsubscribe";
import VerifyEmail from "./pages/VerifyEmail";
import Instalar from "./pages/Instalar";

// Admin Password Control System Pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOperador = lazy(() => import("./pages/admin/Operador"));
const AdminGiras = lazy(() => import("./pages/admin/Giras"));
const AdminMediuns = lazy(() => import("./pages/admin/Mediuns"));
const AdminConsulentes = lazy(() => import("./pages/admin/Consulentes"));
const AdminRelatorios = lazy(() => import("./pages/admin/Relatorios"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const Admin = lazy(() => import("./pages/Admin"));

const queryClient = new QueryClient();


const App = () => (
  <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Carregando...</div>}>
    <ThemeProvider defaultTheme="system" storageKey="tupja-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/sobre-umbanda" element={<SobreUmbanda />} />
              <Route path="/quem-somos" element={<QuemSomos />} />
              <Route path="/trabalhos-espirituais" element={<TrabalhosEspirituais />} />
              <Route path="/normas" element={<Normas />} />
              <Route path="/doacoes" element={<Doacoes />} />
              <Route path="/galeria" element={<Galeria />} />
              <Route path="/biblioteca" element={<Biblioteca />} />
              <Route path="/contato" element={<Contato />} />
              
              {/* Password Control System - Admin Routes */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/operador" element={<AdminOperador />} />
              <Route path="/admin/giras" element={<AdminGiras />} />
              <Route path="/admin/mediuns" element={<AdminMediuns />} />
              <Route path="/admin/consulentes" element={<AdminConsulentes />} />
              <Route path="/admin/relatorios" element={<AdminRelatorios />} />
              <Route path="/admin/usuarios" element={<AdminUsers />} />
              
              {/* Integrated Admin Pages */}
              <Route path="/admin/atendimentos" element={<Admin tab="appointments" />} />
              <Route path="/admin/calendario" element={<Admin tab="calendar" />} />
              <Route path="/admin/gira-semanal" element={<Admin tab="weekly" />} />
              <Route path="/admin/galeria" element={<Admin tab="gallery" />} />
              <Route path="/admin/biblioteca" element={<Admin tab="library" />} />
              <Route path="/admin/comunicados" element={<Admin tab="newsletter" />} />
              <Route path="/admin/push" element={<Admin tab="notifications" />} />
              <Route path="/admin/whatsapp" element={<Admin tab="whatsapp" />} />
              <Route path="/admin/posts" element={<Admin tab="instagram" />} />
              <Route path="/admin/membros-site" element={<Admin tab="members" />} />
              <Route path="/admin/configuracoes" element={<Admin tab="settings" />} />

              <Route path="/auth" element={<Auth />} />

              <Route path="/agendamento" element={<Agendamento />} />
              <Route path="/agendamento-seu-ze" element={<Agendamento />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/verificar-email" element={<VerifyEmail />} />
              <Route path="/instalar" element={<Instalar />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <PWAInstallPrompt />
            <PushNotificationPrompt />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </Suspense>
);

export default App;
